import { useCallback, useEffect, useMemo, useRef } from 'react';
import { kanal, type Mitglied } from '@beholder/shared';
import {
  heuteIso,
  istUeberfaellig,
  tageZwischen,
  type Aufgabe,
  type AufgabeDetail,
  type AufgabenLive,
  type MeineAufgabe,
  type MindmapAuswahl,
} from '@beholder/shared/module/aufgaben';
import { api, modulPfad } from '../../core/api';
import { live, useLive, useLiveVerbunden } from '../../core/live';
import { useDaten } from '../../core/ui';

export const pfad = (projektId: string, rest = '') => modulPfad(projektId, 'aufgaben', rest);

const PRIO_RANG = { hoch: 0, normal: 1, niedrig: 2 } as const;

/** Gleiche Reihenfolge wie der Server: Erledigte zuletzt, dann nach Fälligkeit, Priorität, Alter. */
export function sortieren(liste: Aufgabe[]): Aufgabe[] {
  return [...liste].sort((a, b) => {
    const e = Number(a.status === 'erledigt') - Number(b.status === 'erledigt');
    if (e) return e;
    if (a.faellig !== b.faellig) {
      if (!a.faellig) return 1;
      if (!b.faellig) return -1;
      return a.faellig < b.faellig ? -1 : 1;
    }
    const p = PRIO_RANG[a.prioritaet] - PRIO_RANG[b.prioritaet];
    if (p) return p;
    return a.erstelltAm < b.erstelltAm ? -1 : a.erstelltAm > b.erstelltAm ? 1 : 0;
  });
}

/** Wird bei jeder Wiederverbindung aufgerufen (nicht beim ersten Verbinden). */
export function useNachWiederverbindung(fn: () => void) {
  const verbunden = useLiveVerbunden();
  const vorher = useRef(verbunden);
  const ref = useRef(fn);
  ref.current = fn;
  useEffect(() => {
    if (verbunden && !vorher.current) ref.current();
    vorher.current = verbunden;
  }, [verbunden]);
}

/** Alle Aufgaben eines Projekts, live aktuell gehalten. */
export function useAufgaben(projektId: string) {
  const { daten, setDaten, fehler, laedt, neuLaden } = useDaten(() => api.get<Aufgabe[]>(pfad(projektId)), [projektId]);

  const ersetzen = useCallback(
    (a: Aufgabe) =>
      setDaten((alt) => {
        if (!alt) return alt;
        const i = alt.findIndex((x) => x.id === a.id);
        if (i < 0) return [...alt, a];
        // Ältere Stände (z. B. verspätete Live-Nachricht) nicht über neuere legen
        if (alt[i]!.version > a.version || (alt[i]!.version === a.version && alt[i]!.geaendertAm > a.geaendertAm)) return alt;
        const neu = [...alt];
        neu[i] = a;
        return neu;
      }),
    [setDaten],
  );
  const entfernen = useCallback((id: string) => setDaten((alt) => alt?.filter((x) => x.id !== id) ?? alt), [setDaten]);

  useLive(kanal(projektId, 'aufgaben'), (d) => {
    const n = d as AufgabenLive;
    if (n.typ === 'aufgabe') ersetzen(n.aufgabe);
    if (n.typ === 'geloescht') entfernen(n.id);
    if (n.typ === 'kommentare') window.dispatchEvent(new CustomEvent('auf:kommentare', { detail: n }));
    if (n.typ === '__anlagen' && n.bezug) {
      const id = n.bezug;
      window.dispatchEvent(new CustomEvent('auf:anlagen', { detail: id }));
      // Anlagen-Zahl dieser Aufgabe neu holen
      void api
        .get<AufgabeDetail>(pfad(projektId, `/${id}`))
        .then(({ kommentare: _k, ...a }) => ersetzen(a))
        .catch(() => undefined);
    }
  });
  useNachWiederverbindung(() => void neuLaden());

  const liste = useMemo(() => (daten ? sortieren(daten) : null), [daten]);
  return { liste, fehler, laedt, neuLaden, ersetzen, entfernen };
}

/** „Meine Aufgaben“ über alle Projekte, live: jedes beteiligte Projekt wird abonniert. */
export function useMeineAufgaben(erledigte = false, zusaetzlichesProjekt?: string) {
  const { daten, fehler, neuLaden } = useDaten(
    () => api.get<MeineAufgabe[]>(`/api/modul/aufgaben/meine${erledigte ? '?erledigte=1' : ''}`),
    [erledigte],
  );
  const projekte = useMemo(
    () => [...new Set([...(zusaetzlichesProjekt ? [zusaetzlichesProjekt] : []), ...(daten ?? []).map((a) => a.projektId)])].sort(),
    [daten, zusaetzlichesProjekt],
  );
  const schluessel = projekte.join(',');
  useEffect(() => {
    let zeit: ReturnType<typeof setTimeout> | undefined;
    const spaeter = () => {
      clearTimeout(zeit);
      zeit = setTimeout(() => void neuLaden(), 250);
    };
    const ab = schluessel ? schluessel.split(',').map((p) => live.abonnieren(kanal(p, 'aufgaben'), spaeter)) : [];
    return () => {
      clearTimeout(zeit);
      ab.forEach((f) => f());
    };
  }, [schluessel, neuLaden]);
  useNachWiederverbindung(() => void neuLaden());
  return { liste: daten, fehler, neuLaden };
}

export function useMitglieder(projektId: string) {
  const { daten } = useDaten(() => api.get<Mitglied[]>(`/api/projekte/${projektId}/mitglieder`), [projektId]);
  return useMemo(
    () => (daten ?? []).map((m) => ({ id: m.benutzerId, name: m.anzeigename })).sort((a, b) => a.name.localeCompare(b.name, 'de')),
    [daten],
  );
}

export function useMindmap(projektId: string): MindmapAuswahl {
  const { daten } = useDaten(
    () => api.get<MindmapAuswahl>(pfad(projektId, '/mindmap-knoten')).catch(() => ({ verfuegbar: false, knoten: [] })),
    [projektId],
  );
  return daten ?? { verfuegbar: false, knoten: [] };
}

// ---------------------------------------------------------------- Anzeigehilfen

export function faelligText(faellig: string, heute = heuteIso()): string {
  const tage = tageZwischen(heute, faellig);
  if (tage === 0) return 'heute';
  if (tage === 1) return 'morgen';
  if (tage === -1) return 'gestern';
  const [j, m, t] = faellig.split('-').map(Number) as [number, number, number];
  const d = new Date(j, m - 1, t);
  const mitJahr = faellig.slice(0, 4) !== heute.slice(0, 4);
  return d.toLocaleDateString('de-DE', { weekday: 'short', day: 'numeric', month: 'numeric', ...(mitJahr ? { year: 'numeric' } : {}) });
}

export function ueberfaelligText(a: Aufgabe, heute = heuteIso()): string | null {
  if (!istUeberfaellig(a, heute) || !a.faellig) return null;
  const tage = -tageZwischen(heute, a.faellig);
  return tage === 1 ? 'seit gestern überfällig' : `seit ${tage} Tagen überfällig`;
}

export function kuerzel(name: string): string {
  const teile = name.trim().split(/\s+/).filter(Boolean);
  if (teile.length >= 2) return (teile[0]![0]! + teile[teile.length - 1]![0]!).toUpperCase();
  return (name.trim().slice(0, 2) || '?').toUpperCase();
}
